/* globals paypal */
import React, { memo, useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import braintree from "braintree-web";

import TotalSavings from "components/checkout/TotalSavings";

const BILLING_AGREEMENT_DESCRIPTION =
  "Literati delivers the world's best children's books directly to your door. You can view our Terms of Service at literati.com/terms.";

/**
 * Provides a button with encapsulated paypal logic.
 *
 * @param {Object} props
 * @param {Function} props.onToken
 * @param {Function} props.onError
 * @param {Object} props.error
 * @param {String} props.orderTotal
 * @param {Boolean} [props.hideTotal]
 * @param {String} [props.className]
 */
const CheckoutPayPalButton = ({
  onToken,
  onError,
  error,
  orderTotal,
  hideTotal,
  showSavings,
  savings,
  className,
}) => {
  const paypalDeviceDataInput = useRef();
  const [token, setToken] = useState();
  const braintreeInstance = useRef();
  const paypalInstance = useRef();

  // handle token
  useEffect(() => {
    if (token) {
      onToken({ token });
      setToken(null);
    }
  }, [token, onToken]);

  const setup = useCallback(() => {
    braintree.client.create(
      {
        authorization: process.env.NEXT_PUBLIC_BRAINTREE_TOKENIZATION_KEY,
      },
      function (clientErr, clientInstance) {
        if (clientErr) {
          console.error("Error creating braintree client:", clientErr);
          return;
        }
        braintreeInstance.current = clientInstance;
        // Set paypal data collector value
        braintree.dataCollector.create(
          {
            paypal: true,
            client: clientInstance,
          },
          (err, dataCollectorInstance) => {
            if (err) {
              console.error("Error creating paypal dataCollector:", err);
              return;
            }
            if (braintreeInstance.current) {
              paypalDeviceDataInput.current.value =
                dataCollectorInstance.deviceData;
            } else {
              dataCollectorInstance.teardown();
            }
          }
        );

        braintree.paypalCheckout.create(
          {
            client: clientInstance,
          },
          function (paypalCheckoutErr, paypalCheckoutInstance) {
            paypalInstance.current = paypalCheckoutInstance;
            if (paypalCheckoutErr) {
              console.error(
                "Error creating PayPal Checkout:",
                paypalCheckoutErr
              );
              return;
            }

            paypalCheckoutInstance.loadPayPalSDK(
              {
                vault: true,
                intent: "tokenize",
              },
              function () {
                paypal
                  .Buttons({
                    fundingSource: paypal.FUNDING.PAYPAL,

                    style: {
                      label: "pay",
                      size: "large",
                      shape: "pill",
                      color: "blue",
                    },

                    createBillingAgreement: function () {
                      return paypalCheckoutInstance.createPayment({
                        flow: "vault", // Required
                        billingAgreementDescription:
                          BILLING_AGREEMENT_DESCRIPTION,
                        enableShippingAddress: false,
                      });
                    },

                    onApprove: function (data) {
                      return paypalCheckoutInstance
                        .tokenizePayment(data)
                        .then((payload) => {
                          setToken(payload.nonce);
                        });
                    },

                    onCancel: (data) => {
                      console.warn(
                        "paypal button payment cancelled",
                        JSON.stringify(data, 0, 2)
                      );
                    },

                    onError: (err) => {
                      if (braintreeInstance.current) {
                        // If we're mounted, log and display error.
                        console.error("paypal button payment error", err);
                        onError(
                          "The PayPal network had an error. Please " +
                            "reload the page and try again."
                        );
                      } else {
                        const buttonMissing = new RegExp(
                          "^Document is ready and element .* does not exist"
                        ).test(err.stack);
                        if (!buttonMissing) {
                          // Just log unexpected errors when unmounted.
                          console.error("paypal button error", err);
                        }
                      }
                    },
                  })
                  .render("#paypal-button")
                  .then(function () {
                    // The PayPal button will be rendered in an html element with the ID
                    // `paypal-button`. This function will be called when the PayPal button
                    // is set up and ready to be used
                  });
              }
            );
          }
        );
      }
    );
  }, []);

  useEffect(() => {
    setup();
    return () => {
      if (braintreeInstance.current && paypalInstance.current) {
        braintreeInstance.current.teardown();
        paypalInstance.current.teardown();
      }
    };
  }, [setup, braintreeInstance, paypalInstance]);

  return (
    <form className={className}>
      <input
        type="hidden"
        name="paypal_device_data"
        ref={paypalDeviceDataInput}
      />
      {!hideTotal && (
        <div className="flex justify-between pt-6 pb-4">
          <div className="font-medium text-text-textGray-darker pr-3">
            Today&apos;s Total
          </div>
          <div className="font-medium text-textGray-darker">{orderTotal}</div>
        </div>
      )}
      {showSavings && (
        <div className="mb-4">
          <TotalSavings savings={savings} />
        </div>
      )}
      <div className={"btn-wrapper"}>
        <div id="paypal-button" className={"text-center"} />
      </div>
      <div className={"text-sm text-error mt-1"}>{error}</div>
    </form>
  );
};

CheckoutPayPalButton.propTypes = {
  onToken: PropTypes.func.isRequired,
  onError: PropTypes.func.isRequired,
  error: PropTypes.object,
  orderTotal: PropTypes.string,
  error: PropTypes.string,
  hideTotal: PropTypes.bool,
  showSavings: PropTypes.bool,
};

export default memo(CheckoutPayPalButton);
